import NpmLoader from './loader/npm-loader';
import { loadFrameworkPreset } from './loader/preset-loader';
import { getAllPresetNames } from './preset-registry';
import moduleRegistry, { CDN_MODULES } from './module-registry';
import notifier from './notifier';
/**
 * 依赖打包编排器（仅编辑器使用）
 *
 * 维护 pageDeps（页面级依赖追踪），编排远程打包，
 * 并委托 ModuleRegistry 执行 bundle。
 */
class DependencyLoader {
    pageDeps = new Map();
    framework = 'vue3';
    npmLoader = new NpmLoader();
    initialized = false;
    /**
     * 初始化：加载 framework preset，写入已有的页面级依赖
     *
     * pages 来自 projectInfo.dependency.pages，
     * 写入 pageDeps 后 DependencyLoader 从一开始就持有全量依赖视图。
     */
    async init(framework = 'vue3', pages, packageJson) {
        this.framework = framework;
        this.npmLoader.setFramework(framework);
        if (packageJson) {
            this.npmLoader.packageJson = packageJson;
        }
        if (pages) {
            const presets = getAllPresetNames(framework);
            Object.entries(pages).forEach(([pageId, deps]) => {
                const filtered = deps.filter(dep => !dep.startsWith('@wuji-gogo') && !presets.includes(dep));
                this.pageDeps.set(pageId, new Set(filtered));
            });
        }
        await loadFrameworkPreset(framework);
        this.initialized = true;
    }
    /**
     * 声明式设置某页面的依赖
     *
     * 自动与已打包的依赖集合做 diff，
     * 有新增依赖时触发远程打包（带 500ms 防抖）。
     */
    async load(pageId, dependencies) {
        const presets = getAllPresetNames(this.framework);
        const filtered = dependencies.filter(dep => !dep.startsWith('@wuji-gogo') && !presets.includes(dep));
        this.pageDeps.set(pageId, new Set(filtered));
        const npmDeps = this.getNpmDeps();
        const cdnDeps = this.getCdnDeps();
        if (!npmDeps.length && !cdnDeps.length)
            return;
        if (cdnDeps.length) {
            await moduleRegistry.load({ dependencies: cdnDeps });
        }
        const cached = this.npmLoader.checkNeedsRebuild(npmDeps);
        if (cached)
            return;
        const result = await this.npmLoader.bundle(npmDeps);
        notifier.emitDepsChanged({
            allDependencies: this.getAllDependencies(),
            bundle: result.bundle,
            packageJson: result.packageJson,
        });
    }
    /**
     * 根据 pageDeps 当前状态重新打包（如果有变化）
     *
     * 内部对比 allDependencies + packageJson 与上次打包结果，
     * 一致则直接返回缓存，不触发远程打包。
     */
    async save(options) {
        const npmDeps = this.getNpmDeps();
        if (!npmDeps.length) {
            return { bundle: { js: '', css: '' }, packageJson: '' };
        }
        const cached = this.npmLoader.checkNeedsRebuild(npmDeps, options?.packageJson);
        if (cached)
            return cached;
        return this.npmLoader.bundle(npmDeps, options?.packageJson, true);
    }
    /**
     * 返回当前状态
     */
    getInfo() {
        return {
            bundle: { ...this.npmLoader.bundleFile },
            packageJson: this.npmLoader.packageJson,
            allDependencies: this.getAllDependencies(),
        };
    }
    /**
     * 是否已触发过打包
     */
    hasBundled() {
        return Boolean(this.npmLoader.bundleFile.js || this.npmLoader.bundleFile.css);
    }
    getAllDependencies() {
        const all = new Set();
        for (const deps of this.pageDeps.values()) {
            deps.forEach(dep => all.add(dep));
        }
        return Array.from(all);
    }
    getNpmDeps() {
        return this.getAllDependencies().filter(dep => !CDN_MODULES.includes(dep));
    }
    getCdnDeps() {
        return this.getAllDependencies().filter(dep => CDN_MODULES.includes(dep));
    }
}
const dependencyLoader = new DependencyLoader();
window.wuji_dependency_loader = {
    register: moduleRegistry.register.bind(moduleRegistry),
    load: moduleRegistry.load.bind(moduleRegistry),
    onProgress: notifier.onProgress.bind(notifier),
};
export default dependencyLoader;
